/**
 * Device telemetry + remote control for the Live Monitor cards.
 *
 * Reuses the existing telemetry service (web_server/routes/api/telemetry.js,
 * the same one behind the partner / telemetry dashboards):
 *   GET  api/telemetry/live                 every device's last reported state
 *   POST api/telemetry/command              queue a command + push it to the phone (FCM)
 *   GET  api/telemetry/commands?device_id=  command log with the app's acknowledgement
 *
 * Why this matters on a monitoring screen: the live ECG travels over the app's
 * own socket, but commands travel over FCM push — a separate path that still
 * reaches the phone when the stream has dropped. So a disconnected patient can
 * be told to reconnect from here, without anyone touching the phone.
 *
 * ONE shared poll serves the whole board: `live` returns every device in one
 * response, so polling it per card would multiply the load by the number of
 * patients for no gain. Cards subscribe; the poll runs only while someone is
 * subscribed.
 */
import axios from 'axios';
import { telemetryApi } from '../../config/config';

const POLL_MS = 10000;

let byDevice = {};          // String(device_id) -> live row
let byUser = {};            // String(user_id)   -> live row
let lastError = null;       // { status, message } of the last failed poll
let lastOkAt = 0;
let timer = null;
let inFlight = false;
const subscribers = new Set();

function describe(err) {
  const status = err && err.response && err.response.status;
  if (status === 401) return { status, message: 'Not signed in — log in again.' };
  if (status === 403) return { status, message: 'Your login is not allowed to see device telemetry or send commands (administrator access is required).' };
  if (status === 404) return { status, message: 'The telemetry service is not available on the server.' };
  if (status) return { status, message: `Telemetry service error (HTTP ${status}).` };
  return { status: 0, message: 'Telemetry service unreachable.' };
}

function notify() { subscribers.forEach(fn => fn()); }

export function refreshTelemetry() {
  if (inFlight) return Promise.resolve();
  inFlight = true;
  return axios.get(`${telemetryApi}api/telemetry/live`)
    .then(res => {
      const list = (res.data && res.data.devices) || [];
      const d = {}, u = {};
      list.forEach(row => {
        if (row.device_id != null) d[String(row.device_id)] = row;
        if (row.user_id != null) u[String(row.user_id)] = row;
      });
      byDevice = d; byUser = u; lastError = null; lastOkAt = Date.now();
    })
    .catch(err => { lastError = describe(err); })
    .then(() => { inFlight = false; notify(); });
}

/** Subscribe a card. Starts the shared poll on the first subscriber, stops it on the last. */
export function subscribeTelemetry(fn) {
  subscribers.add(fn);
  if (!timer) {
    refreshTelemetry();
    timer = setInterval(refreshTelemetry, POLL_MS);
  } else if (Date.now() - lastOkAt > POLL_MS) {
    refreshTelemetry();
  }
  return () => {
    subscribers.delete(fn);
    if (!subscribers.size && timer) { clearInterval(timer); timer = null; }
  };
}

export function getTelemetry(deviceId, patientId) {
  return byDevice[String(deviceId)] || (patientId ? byUser[String(patientId)] : null) || null;
}
export function getTelemetryError() { return lastError; }

// The telemetry service keys a patient by the 6-digit number; anything else
// (a test device like "SIM-TLS") can only be addressed by the patient's _id.
const numericId = (deviceId) => (/^\d+$/.test(String(deviceId || '')) ? String(deviceId) : null);

export function sendDeviceCommand(deviceId, patientId, command, params) {
  let who = 'live-monitor';
  try { who = window.localStorage.getItem('verifiername') || who; } catch (e) { /* storage blocked */ }
  return axios.post(`${telemetryApi}api/telemetry/command`, {
    device_id: numericId(deviceId),
    user_id: patientId || null,
    command: command,
    params: params || undefined,
    requestedBy: who,
  }).then(res => res.data || {}).catch(err => { throw describe(err); });
}

export function fetchDeviceCommands(deviceId, patientId) {
  const p = new URLSearchParams();
  if (numericId(deviceId)) p.set('device_id', numericId(deviceId));
  else if (patientId) p.set('user_id', patientId);
  return axios.get(`${telemetryApi}api/telemetry/commands?` + p.toString())
    .then(res => ((res.data && res.data.commands) || []))
    .catch(err => { throw describe(err); });
}

/**
 * Commands offered on a monitoring card. Deliberately NOT here: clear_memory
 * and deactivate (factory reset). They destroy data and belong on the
 * administration page, not one click away on a screen people watch all shift.
 */
export const CARD_COMMANDS = [
  { cmd: 'connect',         label: 'Connect device',  hint: 'Tell the app to connect to the ECG device over Bluetooth' },
  { cmd: 'poll',            label: 'Wake / poll app', hint: 'Wake the app and make it report its status now' },
  { cmd: 'mode_remote',     label: 'Remote monitoring', hint: 'Put the app in Remote Monitoring mode (live streaming)' },
  { cmd: 'sync_now',        label: 'Sync now',        hint: 'Upload any ECG the phone is still holding' },
  { cmd: 'start_recording', label: 'Start recording', hint: 'Start recording on the ECG device' },
  { cmd: 'stop_recording',  label: 'Stop recording',  hint: 'Stop recording on the ECG device', confirm: 'Stop recording for this patient? Monitoring data will stop until it is started again.' },
  { cmd: 'disconnect',      label: 'Disconnect',      hint: 'Drop the Bluetooth link to the ECG device', confirm: 'Disconnect the ECG device from the phone? The live ECG will stop until it is connected again.' },
];

/** How delivery / acknowledgement reads to a person. */
export function describeCommandState(c) {
  if (!c) return '';
  if (c.status === 'done' || c.status === 'executed' || c.status === 'ack' || c.status === 'acked') return 'done by the app';
  if (c.status === 'failed' || c.status === 'error') return 'the app reported a failure';
  if (c.delivery === 'pushed' || c.status === 'sent') return 'sent to the phone — waiting for the app';
  if (c.delivery === 'no_token') return 'queued — this phone has no push registration (app not logged in?)';
  if (c.delivery === 'push_failed') return 'queued — push to the phone failed';
  return 'queued';
}
